<script setup>
import { getCreditCardCompany, formatCardNumber } from '@/utils/credit-format'

const emits = defineEmits(['update:modelValue'])
const props = defineProps({
  modelValue: { type: Boolean, default: false }
})

const route = useRoute()
const router = useRouter()
const $payment = usePayment()
const { $dayjs } = useNuxtApp()
const { getPoint, getPlans, getSettings } = useDatabase()
const { loading } = useLoading()
const { notification } = useNotifications()
const sPoints = useState('points')
const sAccount = useState('account')

const plans   = ref()
const payment = ref()

const selectedPlan = ref(null)
const dialog = reactive({
  plans: props.modelValue,
  card: false,
})

const cardInit = {
  number: null,
  exp_month: null,
  exp_year: null,
  cvc: null,
  name: null,
}
const cardInput = ref({...cardInit})
const cardSave  = ref(false)
const cardCorp  = computed(() => getCreditCardCompany(cardInput.value.number))

const linkToPayment = async (plan) => {
  if (!sAccount.value) {
    router.push({
      path: '/login',
      query: { redirect_to: route.path }
    })
    return
  }

  if (!payment.value) {
    payment.value = await getSettings('payment')
  }

  if (payment.value.service === 'payjp') {
    selectedPlan.value = plan
    dialog.card = true
  } else {
    selectedPlan.value = plan
    $payment[payment.value.service].createSession({
      plan_id: plan.id,
      success_url: location.href,
      cancel_url: location.href,
    }).then(session => {
      if (session?.url) location.href = session?.url || '/'
    }).catch(e => {
      console.error(e)
      notification.error(`問題が発生しました。`, 3)
    }).finally(_ => {
      selectedPlan.value = null
    })
  }
}

const onPayjp = async () => {
  try {
    loading.start()

    await $payment.payjp.charge.create({
      account_id: sAccount.value.id,
      plan_id   : selectedPlan.value?.id,
      card      : cardInput.value,
      card_save : cardSave.value,
      back_url  : `${location.protocol}//${location.host}/point/complete`
    }).then(async resp => {
      // console.log(resp)
      if (resp.redirectUrl) {
        location.href = resp.redirectUrl
      } else {
        sPoints.value = await getPoint().then(({total}) => total)
        notification.success(`${selectedPlan.value.point}ポイントがチャージされました。`, 3)
        location.reload()
        dialog.plans = false
      }
    })
  } catch(e) {
    notification.error(`ポイントチャージに失敗しました。`, 3)
    console.error(e)
  } finally {
    loading.stop()
  }
}

watch(() => props.modelValue, aft => dialog.plans = aft)

watch(() => dialog.plans, async aft => {
  emits('update:modelValue', aft)
  if (aft) {
    if (!plans.value) {
      plans.value = await getPlans()
    }
  } else {
    dialog.card = aft
    selectedPlan.value = null
  }
})

watch(() => dialog.card, aft => {
  if (!aft) {
    cardInput.value = {...cardInit}
    selectedPlan.value = null
  }
})
</script>

<template>
  <div>
    <slot></slot>

    <default-ui-dialog
      v-model="dialog.plans"
      title-center
      content-center
      :width="600"
    >
      <template #title>ポイント購入</template>


      <div v-if="sAccount" class="w-full text-center mb-6">
        <p v-if="!isNaN(sPoints)" class="inline-block mx-auto border-b border-zinc pb-1 px-3">
          <small class="w-6 h-6 font-bold inline-flex items-center justify-center rounded-full border-4 border-double border-zinc p-1 mr-1">P</small>
          <strong class="text-lg">{{ sPoints.toLocaleString() }}<small class="font-bold">ポイント</small></strong>
        </p>
        <icon v-else icon="mdi:loading" width="22" class="animate-spin" />
      </div>

      <p class="block sm:hidden text-center text-primary text-sm mb-3">※価格は税抜表示です。</p>

      <div v-if="plans" class="flex flex-col sm:flex-row items-stretch justify-center divide-y-2 sm:divide-y-0 divide-x-0 sm:divide-x-2 divide-dotted divide-zinc-400 text-center">
        <div
          v-for="(plan, p) of plans"
          :key="p"
          class="w-full sm:w-80 py-3 px-3 flex flex-col"
        >
          <NuxtImg v-if="plan.image_file?.url" :src="plan.image_file?.url" class="mt-auto mx-auto h-14 md:h-8" />
          <p class="block text-lg font-noto-serif">{{ plan.point?.toLocaleString() }}ポイント</p>
          <p class="block text-base font-noto-serif">({{ plan.price?.toLocaleString() }}円)</p>
          <button
            type="button"
            class="mt-4 btn btn-sm btn-primary"
            :disabled="selectedPlan"
            @click="linkToPayment(plan)"
          >
            <icon v-if="selectedPlan?.id == plan.id" icon="mdi:loading" class="animate-spin" width="22" />
            <span v-else>購入する</span>
          </button>
        </div>
      </div>
      <p class="hidden sm:block text-center text-primary text-sm mt-3">※価格は税抜表示です。</p>
    </default-ui-dialog>

    <default-ui-dialog
      v-if="sAccount"
      v-model="dialog.card"
      is-form
      title-center
      content-center
      :width="400"
      @submit="onPayjp"
    >
      <template #title>ポイント購入</template>

      <div
        v-if="selectedPlan"
        class="flex items-start gap-x-3 justify-center"
      >
        <NuxtImg :src="selectedPlan.image_file?.url || '/noimage.png'" width="100px" />

        <div class="mb-3">
          <p v-if="selectedPlan.name" class="text-lg font-bold mb-3">{{ selectedPlan.name }}</p>
          <p class="block text-lg font-noto-serif">{{ selectedPlan.point?.toLocaleString() }}ポイント</p>
          <p class="block text-base font-noto-serif">({{ selectedPlan.price?.toLocaleString() }}円)</p>
        </div>
      </div>
      <p class="text-primary text-sm text-center">※価格は税抜表示です。</p>

      <hr class="my-6 border-none border-b border-zinc-300" />

      <default-form-input
        class="mb-6"
        label="カード番号"
        >
        <!-- :message="formatCardNumber(cardInput.number)" -->
        <div class="input-group">
          <input
            required
            v-model="cardInput.number"
            class="input flex-1"
            pattern="\d*"
            inputmode="numeric"
            maxlength="20"
          >
          <!-- <span
            v-if="cardCorp"
            class="input-group-label"
          >{{cardCorp}}</span> -->
        </div>
      </default-form-input>

      <default-form-input class="mb-6" label="カード名義">
        <input
          required
          v-model="cardInput.name"
          class="input"
          placeholder="TARO ANSHIN"
        >
      </default-form-input>
      <div class="flex gap-x-3 justify-between mb-6">
        <default-form-input label="月/年">
          <div class="input-group">
            <select
              required
              v-model="cardInput.exp_month"
              class="select w-16"
            >
              <option :value="null">MM</option>
              <option
                v-for="n in 12"
                :value="n"
                :label="n"
              >{{ n }}</option>
            </select>
            <span class="input-group-label">/</span>
            <select
              required
              v-model="cardInput.exp_year"
              class="select w-20"
            >
              <option :value="null">20YY</option>
              <option
                v-for="n in Array.from({ length: 10 }, (_, i) => ($dayjs().year()) + i)"
                :value="n"
                :label="n"
              >{{ n }}</option>
            </select>
          </div>
        </default-form-input>

        <default-form-input label="CVC">
          <input
            required
            v-model="cardInput.cvc"
            class="input input-sm text-right w-24"
            pattern="\d*"
            maxlength="4"
            type="password"
            inputmode="numeric"
            placeholder="***"
          />
        </default-form-input>
      </div>

      <template #footer>
        <button
          type="button"
          class="mt-4 btn btn-sm btn-zinc btn-outlined"
          @click="dialog.card = false"
        >キャンセル</button>

        <button
          type="submit"
          class="mt-4 btn btn-sm btn-primary"
        >購入する</button>
      </template>
    </default-ui-dialog>
  </div>
</template>